import {
  DialogService as PrimeNgDialogService,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { DialogService as CustomDialogService } from '../../shared/services/dialog.service';
import { rightDynamicModalConfig } from '../../shared/helpers/modal.config';
import { OskAddNewComponent } from './osk-add-new/osk-add-new.component';
import { Component, inject, OnDestroy } from '@angular/core';
import { OsksService } from '../../../api/api-services/osks.service';
import { OskService } from '../../../api/api-services/osk.service';
import { SortOrder } from '../../../api/sort-order.model';
import { eModalRoles } from '../../shared/models/modals';
import { IOskVm } from '../../../api/osk-vm.model';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { MessageService } from 'primeng/api';
import { OskApprovalService } from '../../../api/api-services/osk-approval.service';

@Component({
  selector: 'app-osk',
  templateUrl: './osk.component.html',
  styleUrl: './osk.component.scss',
})
export class OskComponent implements OnDestroy {
  private _osksService = inject(OsksService);
  private _oskService = inject(OskService);
  private _messageService = inject(MessageService);
  private _oskApprovalService = inject(OskApprovalService);
  private _primengDialogService = inject(PrimeNgDialogService);
  private _customDialogService = inject(CustomDialogService);
  private readonly searchChanges = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private request?: Subscription;
  private ref: DynamicDialogRef | undefined;

  public ID_OSK = 'ID_OSK';
  public ID_NEW_OSK = 'ID_NEW_OSK';
  public oskIndex!: number;
  public oskList: IOskVm[] = [];
  public osk!: IOskVm;
  public onlyPendingApproval = false;
  public search = '';
  public loading = false;
  public first = 0;
  public rows = 50;
  public totalRecords = 0;

  // fas-services-table reports only page/pageSize on lazy load, so the list keeps the API default order (OSK name).
  public oskColumns = [
    { field: 'oskShortCode', header: 'Nazwa Ośrodka szkolenia kierowców', customTemplate: 'oskNameTemplate' },
    { field: 'oskPhones', header: 'Dane kontaktowe', customTemplate: 'contactTemplate' },
    { field: 'oskPlatformSubscriptionPlans', header: 'Ilość dostępnych abonamentów', customTemplate: 'subscriptionsListTemplate' },
    { field: 'studentsCount', header: 'Ilość kursantów' },
    { field: 'actions', header: 'Akcje', columnClass: 'white-space-nowrap', actions: [
      { icoSuffix: 'pi pi-pencil', tooltip: 'Edytuj', buttonClass: 'transparent-button appear',
        callback: (data: IOskVm, rowIndex: number) => this.showDetails(data, rowIndex) },
      { icoSuffix: 'pi pi-trash', tooltip: 'Usuń', buttonClass: 'transparent-button appear',
        callback: (data: IOskVm) => this.removeOSK(data) },
    ] },
  ];

  constructor() {
    this.subscriptions.add(this.searchChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => this.resetAndLoad()));
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.subscriptions.unsubscribe();
    if (this.ref) {
      this.ref.close(); // Closes the dialog if it's still open
      this.ref = undefined;
    }
  }

  onSearch(value: string): void {
    this.search = value;
    this.searchChanges.next(value);
  }

  resetAndLoad(): void {
    this.first = 0;
    this.load({ page: 1, pageSize: this.rows });
  }

  load(event?: { page: number; pageSize: number }): void {
    const page = event?.page ?? Math.floor(this.first / this.rows) + 1;
    this.rows = event?.pageSize ?? this.rows;
    this.first = (page - 1) * this.rows;
    this.request?.unsubscribe();
    this.loading = true;
    this.request = this._osksService
      .get(this.search, '', page, this.rows, SortOrder.Ascending, this.onlyPendingApproval)
      .subscribe({
        next: (result) => {
          this.oskList = result.data ?? [];
          this.totalRecords = result.allElementsCount;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this._messageService.add({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się pobrać listy OSK.' });
        },
      });
  }

  addNewOsk(): void {
    this.ref = this._primengDialogService.open(OskAddNewComponent, {
      ...rightDynamicModalConfig,
    });

    this.ref.onClose.subscribe((res: eModalRoles | undefined) => {
      if (res === eModalRoles.SUCCESS) {
        this.resetAndLoad();
      }
    });
  }

  approveOsk(data: IOskVm): void {
    this._oskApprovalService.put(data.guid).subscribe({
      next: () => {
        this._messageService.add({ severity: 'success', summary: 'OSK zaakceptowane' });
        this.load();
      },
      error: () => this._messageService.add({
        severity: 'error', summary: 'Błąd', detail: 'Nie udało się zaakceptować OSK.'
      })
    });
  }

  removeOSK(data: IOskVm): void {
    this._oskService.delete(data.guid).subscribe({
      next: () => {
        this.resetAndLoad();
      }, error: () => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Wystąpił błąd. Spróbuj ponownie później.',
        });
    }})
  }

  showDetails(data: IOskVm, rowIndex: number): void {
    this.osk = { ...data };
    this.oskIndex = rowIndex;
    this._customDialogService.openDialog(this.ID_OSK);
  }
}
